/**
 * Whose role locks the cache holds.
 *
 * A role list's `assignable` flags are an answer for ONE signed-in person:
 * what they may give. So every role-list query is keyed by the signed-in
 * user (the "viewer") as well as the account, and the role lists are
 * dropped whenever who is asking, or where, changes: sign-out, a different
 * user signing in, an account switch and a location switch that changed
 * the session's permissions. Nobody ever sees another person's locks, not
 * even for the few minutes a cached list would otherwise live.
 *
 * App.tsx reports the signed-in user with `setRoleLockViewer` (via
 * `useRoleLockViewerSync`); the role hooks read it with `useRoleLockViewer`.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react';
import type { QueryClient } from '@tanstack/react-query';

let viewer: string | null = null;
const listeners = new Set<() => void>();

/** The signed-in user the role lists are fetched for (null: nobody). */
export function getRoleLockViewer(): string | null {
  return viewer;
}

/** Sets the signed-in user; subscribers re-key their role queries. */
export function setRoleLockViewer(userId: string | null | undefined): void {
  const next = userId || null;
  if (next === viewer) return;
  viewer = next;
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The signed-in user, for role-list query keys. */
export function useRoleLockViewer(): string | null {
  return useSyncExternalStore(subscribe, getRoleLockViewer, getRoleLockViewer);
}

/** Every cached role list (and with it every lock). */
export const ROLE_LISTS_KEY = ['roles', 'byAccount'] as const;

/** Drops every cached role list, so the next picker asks the server again. */
export function clearRoleLocks(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: ROLE_LISTS_KEY });
}

/**
 * Keeps the viewer in step with the signed-in user and drops the role lists
 * when that user changes (a sign-out is a change to nobody).
 */
export function useRoleLockViewerSync(queryClient: QueryClient, userId: string | null | undefined): void {
  const last = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const next = userId || null;
    if (last.current !== undefined && last.current !== next) clearRoleLocks(queryClient);
    last.current = next;
    setRoleLockViewer(next);
  }, [queryClient, userId]);
}
